import { TextField } from '@main/complykit';
import { dynamicFieldName, EditableInputFieldProps, normalizePattern } from '@main/dynamic-form';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { NotAnswered } from './not-answered';

const types = ['number', 'date', 'email', 'url'] as const;
type InputType = (typeof types)[number];
const isInputType = (type?: string): type is InputType => types.includes(type as InputType);
// A stored answer the native input can't parse (12/05/2026 on a date, 1,000 on a number) shows empty.
const fitsType = (type: InputType, value: string) =>
  type === 'date'
    ? /^\d{4}-\d{2}-\d{2}$/.test(value)
    : type !== 'number' || Number.isFinite(Number(value));

export function EditableInputField({ config, state, labelId }: EditableInputFieldProps) {
  const { control } = useFormContext();

  const validation = useMemo(
    () => ({
      ...config.validation,
      pattern: normalizePattern(config.validation?.pattern),
    }),
    [config.validation],
  );

  return (
    <Controller
      name={config.name}
      control={control}
      rules={validation}
      defaultValue={config.value}
      render={({ field }) => {
        const value = field.value == null ? '' : String(field.value);
        if (state.isReadOnly && !value.trim()) {
          return <NotAnswered />;
        }
        return (
          <TextField
            {...dynamicFieldName({ config, labelId })}
            ref={field.ref}
            name={field.name}
            value={value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            type={
              isInputType(config.type) && (!state.isReadOnly || fitsType(config.type, value))
                ? config.type
                : 'text'
            }
            placeholder={config.placeholder}
            isRequired={state.isRequired}
            isDisabled={state.isDisabled}
            isReadOnly={state.isReadOnly}
            error={state.error}
          />
        );
      }}
    />
  );
}
